/**
 * Dialog — the design system's modal shell: overlay, header with a round close button,
 * scrollable body, optional footer.
 *
 * Ported 1:1 from `tw-components.jsx`. Deviations are mechanical only: `cx` and `Icon`
 * are imported, and `React` is imported because this one uses hooks. One drift, since
 * corrected: `DIALOG_W` was missing `xl` (`max-w-[920px]`) — no consumer had needed it
 * until the returns start-flow dialog's two-column step 2 did.
 *
 * **Not a replacement for `components/layout/FlexibleModal`.** That is the app's
 * established dialog; anything already built on it stays there. This exists because
 * `QuickLookDialog` is built on it upstream, and keeping that pairing intact is what
 * makes the quick look match the reference.
 *
 * Closes on Escape and on a backdrop mousedown. Needs `dnd-overlay-in` and
 * `dnd-dialog-in` from ./keyframes.
 *
 * ─── Divergence: `dismissible` ──────────────────────────────────────────────────
 *
 * Not upstream. `dismissible={false}` ignores Escape and the backdrop and disables the
 * close button (kept in place, so the header doesn't reflow) — for while a request
 * is in flight, so closing can't orphan it or hide its result. Passing
 * `onClose={undefined}` instead would also block closing, but drops the button.
 *
 * ─── Divergence: it portals to `document.body` ──────────────────────────────────
 *
 * Upstream doesn't, because it mounts its dialog once at the app root where nothing can
 * trap it. Here the dialog is opened from a product card deep inside the listing grid,
 * and `fixed inset-0` is **not** enough: the grid carries
 * `[animation:dndRevealUp_…_both]`, and an animation targeting `transform` keeps the
 * element a containing block for fixed descendants for as long as it's in effect — which
 * `fill-mode: both` makes permanent. Without the portal the overlay covers the grid
 * instead of the page.
 *
 * A transform, filter, `contain: paint` or `will-change` on any ancestor does the same,
 * so the portal is the fix rather than hunting them one at a time.
 */
import React from "react";
import { createPortal } from "react-dom";

import { cx } from "./cx";
import { Icon } from "./Icon.jsx";
import "./keyframes";

const DIALOG_W = {
  sm: "max-w-[420px]",
  md: "max-w-[560px]",
  lg: "max-w-[760px]",
  xl: "max-w-[920px]",
};

export function Dialog({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = "md",
  dismissible = true,
  style = {},
  className = "",
}) {
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && onClose && dismissible) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, dismissible]);
  if (!open) return null;
  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      className="dnd-dialog-overlay fixed inset-0 z-[var(--z-modal)] flex items-center justify-center p-[var(--space-6)] bg-[var(--surface-overlay)] backdrop-blur-[4px] animate-[dnd-overlay-in_var(--duration-base)_var(--ease-out)]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && onClose && dismissible) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={style}
        className={cx(
          "dnd-dialog relative bg-card rounded-[var(--radius-surface)] shadow-xl w-full max-h-[90vh] overflow-hidden flex flex-col font-sans animate-[dnd-dialog-in_var(--duration-slow)_var(--ease-spring)]",
          DIALOG_W[size] || DIALOG_W.md,
          className
        )}
      >
        <div className="flex items-start gap-[var(--space-4)] pt-[var(--space-7)] px-[var(--space-7)] pb-[var(--space-4)]">
          <div className="flex-1">
            {title && (
              <h2 className="text-xl font-bold text-ink m-0 tracking-[var(--tracking-tight)]">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-sm text-ink-2 mt-[var(--space-1)] mb-0 leading-[1.5]">
                {subtitle}
              </p>
            )}
          </div>
          {onClose && (
            <button
              aria-label="Close"
              onClick={onClose}
              disabled={!dismissible}
              className="shrink-0 border-0 bg-neutral-100 text-ink-2 w-[36px] h-[36px] rounded-full cursor-pointer inline-flex items-center justify-center [transition:background_var(--duration-fast),transform_var(--duration-base)_var(--ease-spring)] hover:bg-neutral-200 active:scale-[0.88] disabled:opacity-40 disabled:cursor-default disabled:hover:bg-neutral-100 disabled:active:scale-100"
            >
              <Icon name="close" size={20} weight={500} />
            </button>
          )}
        </div>
        <div className="px-[var(--space-7)] overflow-y-auto text-ink-2 text-base leading-[1.6]">
          {children}
        </div>
        {footer && (
          <div className="flex justify-end gap-[var(--space-3)] pt-[var(--space-6)] px-[var(--space-7)] pb-[var(--space-7)]">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
